import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaPlay, FaStop, FaSyncAlt } from 'react-icons/fa';
import { useNotify } from 'react-admin';
import constants from '../../constants';
import { httpClient } from '../../providers/myDataProvider';
import FriendInvitesSection from './FriendInvitesSection';

// GH-662 — OUR debug view of the autonomous step challenge (never HR-facing). Per enabled company:
// pool readiness (how many are on the new app version and can track steps), the cycles with their
// funnel, and three testing levers: start a cycle today, run the daily engine now, end+finalize now.

type Counts = { nudged: number; invited: number; opened: number; joined: number; active: number; eligible: number; left: number; declined: number; groups: number };
type Cycle = {
	_id: string;
	start_date: string;
	end_date: string;
	status: 'active' | 'closed' | 'completed';
	manual?: boolean;
	day_index: number;
	days_left: number;
	counts: Counts;
};
type CompanyRow = {
	company: { _id: string; name: string; step_invite_enabled?: boolean }; // GH-778 — flag shown only if the overview returns it
	months_between_cycles: number;
	next_cycle_start: string | null;
	pool: { members: number; step_ready: number; on_new_version: number; outdated_active: number };
	cycles: Cycle[];
};
type Overview = { min_app_version: string; today: string; companies: CompanyRow[] };

const STATUS_HE: Record<Cycle['status'], string> = { active: 'פעיל', closed: 'נסגר — ממתין לחישוב', completed: 'הושלם' };
const STATUS_COLOR: Record<Cycle['status'], 'success' | 'warning' | 'default'> = { active: 'success', closed: 'warning', completed: 'default' };
const heDate = (iso?: string | null) => (iso ? iso.split('-').reverse().join('.') : '—');

export default function StepChallengeDashboard() {
	const notify = useNotify();
	const [data, setData] = useState<Overview | null>(null);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState<string | null>(null);
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const [detail, setDetail] = useState<Record<string, any>>({});

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await httpClient(`${constants.API_URL}step-challenge/admin/overview`, { method: 'GET' });
			setData(res.json as Overview);
		} catch (e: any) {
			notify(e?.message || 'שגיאה בטעינה', { type: 'error' });
		} finally {
			setLoading(false);
		}
	}, [notify]);

	useEffect(() => {
		load();
	}, [load]);

	const run = async (key: string, url: string, okMsg: string) => {
		setBusy(key);
		try {
			const res = await httpClient(`${constants.API_URL}${url}`, { method: 'POST' });
			notify(`${okMsg}${res.json && typeof res.json === 'object' && 'invited' in res.json ? ` · ${JSON.stringify(res.json)}` : ''}`, { type: 'success' });
			await load();
		} catch (e: any) {
			notify(e?.body?.message || e?.message || 'שגיאה', { type: 'error' });
		} finally {
			setBusy(null);
		}
	};

	const toggleDetail = async (cycleId: string) => {
		const next = !open[cycleId];
		setOpen((o) => ({ ...o, [cycleId]: next }));
		if (next && !detail[cycleId]) {
			try {
				const res = await httpClient(`${constants.API_URL}step-challenge/admin/cycle/${cycleId}`, { method: 'GET' });
				setDetail((d) => ({ ...d, [cycleId]: res.json }));
			} catch (e: any) {
				notify(e?.message || 'שגיאה', { type: 'error' });
			}
		}
	};

	return (
		<Box p={2} dir="rtl">
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
				<Box>
					<Typography variant="h5">אתגר צעדים אוטונומי (GH-662)</Typography>
					<Typography variant="body2" color="text.secondary">
						הפעלה/כיבוי לחברה — בדף החברה ("אתגר צעדים אוטונומי?"). מחזור של 14 יום נפתח ב-1 לחודש, כל N חודשים. גרסת אפליקציה
						מינימלית ל-pool: {data?.min_app_version ?? '…'} · היום {heDate(data?.today)}
					</Typography>
				</Box>
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" startIcon={<FaSyncAlt />} onClick={load} disabled={loading}>
						רענון
					</Button>
					<Tooltip title="מריץ עכשיו את מה שהקרון של 08:30 עושה: תחזוקת מחזורים, הזמנות, הודעות אמצע/סיום, תוצאות">
						<span>
							<Button
								variant="contained"
								startIcon={<FaPlay />}
								disabled={!!busy}
								onClick={() => run('daily', 'step-challenge/admin/run-daily', 'המנוע רץ')}
							>
								הרץ מנוע יומי עכשיו
							</Button>
						</span>
					</Tooltip>
				</Stack>
			</Stack>

			{loading && !data ? (
				<CircularProgress />
			) : !data?.companies.length ? (
				<Typography>אין חברות עם הדגל דלוק.</Typography>
			) : (
				data.companies.map((row) => (
					<Paper key={row.company._id} sx={{ p: 2, mb: 2 }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
							<Box>
								<Typography variant="h6">{row.company.name}</Typography>
								<Typography variant="body2" color="text.secondary">
									כל {row.months_between_cycles} חודש{row.months_between_cycles > 1 ? 'ים' : ''} ·{' '}
									{row.next_cycle_start ? `המחזור הבא: ${heDate(row.next_cycle_start)}` : 'מחזור פעיל כרגע'}
								</Typography>
							</Box>
							<Stack direction="row" spacing={1} alignItems="center">
								<Tooltip title="עובדים פעילים בחברה (לא מומחים, לא גלובליים)">
									<Chip size="small" label={`עובדים ${row.pool.members}`} />
								</Tooltip>
								<Tooltip title="דיווחו שהמכשיר תומך בספירת צעדים (טלמטריה על פתיחת האפליקציה)">
									<Chip size="small" label={`מכשיר תומך ${row.pool.step_ready}`} />
								</Tooltip>
								<Tooltip title={`מתוכם על גרסה ≥ ${data.min_app_version} — זה ה-pool שהמנוע מזמין ממנו`}>
									<Chip size="small" color={row.pool.on_new_version ? 'primary' : 'default'} label={`ב-pool ${row.pool.on_new_version}`} />
								</Tooltip>
								<Tooltip title="פעילים ב-90 יום במובייל על גרסה ישנה — מקבלים פעם במחזור פוש/מייל 'עדכנו כדי להצטרף'">
									<Chip size="small" color={row.pool.outdated_active ? 'warning' : 'default'} label={`גרסה ישנה ${row.pool.outdated_active}`} />
								</Tooltip>
								<Button
									size="small"
									variant="outlined"
									startIcon={<FaPlay />}
									disabled={!!busy || row.cycles.some((c) => c.status === 'active')}
									onClick={() => run(`start-${row.company._id}`, `step-challenge/admin/company/${row.company._id}/start-cycle`, 'מחזור נפתח היום')}
								>
									פתח מחזור היום
								</Button>
							</Stack>
						</Stack>

						<TableContainer sx={{ mt: 1.5 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell />
										<TableCell align="right">תאריכים</TableCell>
										<TableCell align="right">סטטוס</TableCell>
										<TableCell align="right">יום</TableCell>
										<TableCell align="right">קיבלו 'עדכנו'</TableCell>
										<TableCell align="right">הוזמנו</TableCell>
										<TableCell align="right">פתחו</TableCell>
										<TableCell align="right">הצטרפו</TableCell>
										<TableCell align="right">פעילים</TableCell>
										<TableCell align="right">זכאים</TableCell>
										<TableCell align="right">עזבו / דחו</TableCell>
										<TableCell align="right">קבוצות</TableCell>
										<TableCell align="right" />
									</TableRow>
								</TableHead>
								<TableBody>
									{!row.cycles.length ? (
										<TableRow>
											<TableCell colSpan={13} align="right">
												<Typography variant="body2" color="text.secondary">
													עדיין אין מחזורים.
												</Typography>
											</TableCell>
										</TableRow>
									) : null}
									{row.cycles.map((c) => (
										<>
											<TableRow key={c._id} hover>
												<TableCell>
													<Button size="small" onClick={() => toggleDetail(c._id)}>
														{open[c._id] ? <FaChevronUp /> : <FaChevronDown />}
													</Button>
												</TableCell>
												<TableCell align="right">
													{heDate(c.start_date)} – {heDate(c.end_date)}
													{c.manual ? (
														<Chip size="small" label="ידני" sx={{ ml: 1 }} />
													) : null}
												</TableCell>
												<TableCell align="right">
													<Chip size="small" color={STATUS_COLOR[c.status]} label={STATUS_HE[c.status]} />
												</TableCell>
												<TableCell align="right">{c.status === 'active' ? `${c.day_index}/14` : '—'}</TableCell>
												<TableCell align="right">{c.counts.nudged}</TableCell>
												<TableCell align="right">{c.counts.invited}</TableCell>
												<TableCell align="right">{c.counts.opened}</TableCell>
												<TableCell align="right">{c.counts.joined}</TableCell>
												<TableCell align="right">{c.counts.active}</TableCell>
												<TableCell align="right">{c.counts.eligible}</TableCell>
												<TableCell align="right">
													{c.counts.left} / {c.counts.declined}
												</TableCell>
												<TableCell align="right">{c.counts.groups}</TableCell>
												<TableCell align="right">
													{c.status !== 'completed' ? (
														<Tooltip title="בדיקות: מסיים את המחזור היום ומחשב תוצאות מיד (הפושים של התוצאות יוצאים בריצה היומית הבאה)">
															<span>
																<Button
																	size="small"
																	color="warning"
																	startIcon={<FaStop />}
																	disabled={!!busy}
																	onClick={() => run(`end-${c._id}`, `step-challenge/admin/cycle/${c._id}/end-now`, 'המחזור נסגר וחושב')}
																>
																	סיים וחשב עכשיו
																</Button>
															</span>
														</Tooltip>
													) : null}
												</TableCell>
											</TableRow>
											<TableRow key={`${c._id}-detail`}>
												<TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
													<Collapse in={!!open[c._id]} unmountOnExit>
														<Box p={2} bgcolor="#FAFAFA">
															{!detail[c._id] ? (
																<CircularProgress size={20} />
															) : (
																<>
																	{detail[c._id].groups.map((g: any) => (
																		<Box key={g._id} mb={2}>
																			<Typography variant="subtitle2">
																				קבוצה {g.index} · {g.participant_count} משתתפים · {g.status === 'full' ? 'מלאה' : 'פתוחה'}
																			</Typography>
																			<Table size="small">
																				<TableHead>
																					<TableRow>
																						<TableCell align="right">שם</TableCell>
																						<TableCell align="right">מייל</TableCell>
																						<TableCell align="right">גרסה</TableCell>
																						<TableCell align="right">סטטוס</TableCell>
																						<TableCell align="right">מ-</TableCell>
																						<TableCell align="right">ימים</TableCell>
																						<TableCell align="right">ממוצע</TableCell>
																						<TableCell align="right">מחצית 1</TableCell>
																						<TableCell align="right">מחצית 2</TableCell>
																						<TableCell align="right">שיפור</TableCell>
																						<TableCell align="right">זכאי</TableCell>
																						<TableCell align="right">דירוג סופי</TableCell>
																						<TableCell align="right">פרסים</TableCell>
																					</TableRow>
																				</TableHead>
																				<TableBody>
																					{g.participants.map((p: any) => (
																						<TableRow key={p._id}>
																							<TableCell align="right">{p.name || '—'}</TableCell>
																							<TableCell align="right">{p.email}</TableCell>
																							<TableCell align="right">{p.version || '—'}</TableCell>
																							<TableCell align="right">{p.status}</TableCell>
																							<TableCell align="right">{heDate(p.participation_start)}</TableCell>
																							<TableCell align="right">{p.valid_days}</TableCell>
																							<TableCell align="right">{(p.avg_daily || 0).toLocaleString()}</TableCell>
																							<TableCell align="right">{p.first_half_avg != null ? p.first_half_avg.toLocaleString() : '—'}</TableCell>
																							<TableCell align="right">{p.second_half_avg != null ? p.second_half_avg.toLocaleString() : '—'}</TableCell>
																							<TableCell align="right">{p.improvement_pct != null ? `${p.improvement_pct}%` : '—'}</TableCell>
																							<TableCell align="right">{p.eligible ? '✓' : ''}</TableCell>
																							<TableCell align="right">{p.final_rank ?? '—'}</TableCell>
																							<TableCell align="right">{(p.awards || []).join(', ')}</TableCell>
																						</TableRow>
																					))}
																				</TableBody>
																			</Table>
																		</Box>
																	))}
																	{detail[c._id].not_joined?.length ? (
																		<Box>
																			<Typography variant="subtitle2">הוזמנו ולא הצטרפו ({detail[c._id].not_joined.length})</Typography>
																			<Table size="small">
																				<TableHead>
																					<TableRow>
																						<TableCell align="right">שם</TableCell>
																						<TableCell align="right">מייל</TableCell>
																						<TableCell align="right">סטטוס</TableCell>
																						<TableCell align="right">סיבת הזמנה</TableCell>
																						<TableCell align="right">הוזמן</TableCell>
																						<TableCell align="right">פתח</TableCell>
																					</TableRow>
																				</TableHead>
																				<TableBody>
																					{detail[c._id].not_joined.map((p: any, i: number) => (
																						<TableRow key={i}>
																							<TableCell align="right">{p.name || '—'}</TableCell>
																							<TableCell align="right">{p.email}</TableCell>
																							<TableCell align="right">{p.status}</TableCell>
																							<TableCell align="right">{p.invite_reason || '—'}</TableCell>
																							<TableCell align="right">{p.invited_at ? new Date(p.invited_at).toLocaleDateString('he-IL') : '—'}</TableCell>
																							<TableCell align="right">{p.viewed_at ? '✓' : ''}</TableCell>
																						</TableRow>
																					))}
																				</TableBody>
																			</Table>
																		</Box>
																	) : null}
																</>
															)}
														</Box>
													</Collapse>
												</TableCell>
											</TableRow>
										</>
									))}
								</TableBody>
							</Table>
						</TableContainer>
						{/* GH-778 — "אתגר חבר": KPIs + lazily loaded invites table for this company */}
						<FriendInvitesSection
							companyId={row.company._id}
							cycles={row.cycles}
							enabled={row.company.step_invite_enabled}
						/>
					</Paper>
				))
			)}
		</Box>
	);
}
